import { jwtDecode } from 'jwt-decode'
import { normalizeRoles } from '@/utils/permissions'

// Decoded JWT shape from Keycloak — organization is a map { orgId: {...} }
interface KeycloakToken {
  sub?:             string
  email?:           string
  name?:            string
  preferred_username?: string
  organization?:    Record<string, unknown>
  organizationId?:  string   // some realms use this flat claim instead
  org_id?:          string   // alternative claim name
  realm_access?:    { roles: string[] }
  resource_access?: Record<string, { roles?: string[] }>
}

export function extractOrgId(token: string): string | null {
  try {
    const decoded = jwtDecode<KeycloakToken>(token)

    // Strategy 1: organization map { "<orgId>": { ... } } — key IS the org id
    if (decoded.organization && typeof decoded.organization === 'object') {
      const keys = Object.keys(decoded.organization)
      if (keys.length > 0) return keys[0]
    }
    // Strategy 2: flat string claims
    if (decoded.organizationId) return decoded.organizationId
    if (decoded.org_id)         return decoded.org_id

    // Strategy 3: log the full token so we can see the actual claim shape in dev
    if (import.meta.env.DEV) {
      console.warn('[jwtAuth] could not extract orgId from JWT. Claims:', Object.keys(decoded))
    }
    return null
  } catch {
    return null
  }
}

export interface DecodedIdentity {
  id:       string
  name:     string
  email:    string
  roleName: string[]
}

// Derives display identity + this app's custom role list from a Keycloak
// access token. `roleName` here is the real, custom role system
// (iam_org_admin, iam_superadmin, client_individual, ...) — separate from any
// legacy realm-role flag a caller might also want to derive.
export function decodeIdentity(token: string): DecodedIdentity {
  const decoded = jwtDecode<KeycloakToken>(token)
  const realmManagementRoles = decoded.resource_access?.['realm-management']?.roles ?? []
  const roleName = normalizeRoles([
    ...(decoded.realm_access?.roles ?? []),
    ...(realmManagementRoles.some(role => ['realm-admin', 'manage-realm', 'manage-users'].includes(role)) ? ['iam_superadmin'] : []),
  ])
  return {
    id:    decoded.sub ?? 'kc-user',
    name:  decoded.name ?? decoded.preferred_username ?? 'User',
    email: decoded.email ?? decoded.preferred_username ?? '',
    roleName,
  }
}
